import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import LeaveDrawer from "../components/LeaveDrawer";
import { listLeaves, createLeave, updateLeave } from "../api/leaves";
import { createUser, listUsers, updateUser } from "../api/users";
import SettingsPage from "./SettingsPage";
import Table from "../components/Table";
import UserDrawer from "../components/UserDrawer";

const STATUS_LABELS = {
  pending: "Pending",
  approved: "Approved",
  denied: "Rejected",
  rejected: "Rejected",
  cancelled: "Rejected",
};

const TYPE_LABELS = {
  annual: "Annual vacation",
  sick: "Sick leave",
};

const CALENDAR_WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DEFAULT_ANNUAL_LEAVE_DAYS = 30;
const DEFAULT_SICK_LEAVE_DAYS = 14;
const ACCESS_OPTIONS = [
  { key: "inventory", label: "Inventory", helper: "Stock, pricing, and expirations" },
  { key: "invoices", label: "Invoices", helper: "Issue, settle, and export invoices" },
  { key: "suppliers", label: "Suppliers", helper: "Onboard suppliers and edit contacts" },
  { key: "customers", label: "Customers", helper: "Customer accounts and credit notes" },
  { key: "reports", label: "Reports", helper: "Dashboards, exports, and scheduled sends" },
  { key: "employees", label: "Employees", helper: "Invite teammates and manage their access." },
  { key: "settings", label: "Settings", helper: "Organization preferences" },
];

const toDayDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const formatDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const getDurationDays = (start, end) => {
  if (!start || !end) return null;
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null;
  return Math.max(1, Math.round((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1);
};

const normalizeOptionValue = (value) => (value == null ? "" : String(value));

function FilterSelect({ label, value, options = [], onChange, placeholder = "Select", disabled = false }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const normalizedValue = normalizeOptionValue(value);

  const selected = useMemo(
    () => options.find((opt) => normalizeOptionValue(opt.value) === normalizedValue),
    [options, normalizedValue]
  );

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (event) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(event.target)) setOpen(false);
    };
    const handleKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const handleSelect = (optionValue) => {
    if (disabled) return;
    const normalized = normalizeOptionValue(optionValue);
    onChange?.(normalized);
    setOpen(false);
  };

  const displayLabel = selected?.label || placeholder;

  return (
    <div className={`field filter-select ${disabled ? "is-disabled" : ""}`} ref={wrapperRef}>
      <span className="field-label">{label}</span>
      <button
        type="button"
        className="filter-select__control"
        onClick={() => setOpen((prev) => !prev)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`filter-select__value ${selected ? "" : "placeholder"}`}>{displayLabel}</span>
        <span className={`filter-select__chevron ${open ? "open" : ""}`} aria-hidden="true" />
      </button>
      {open ? (
        <div className="dropdown-panel filter-select__menu" role="listbox">
          {options.map((opt, index) => {
            const normalizedOptionValue = normalizeOptionValue(opt.value);
            const isSelected = normalizedOptionValue === normalizedValue;
            return (
              <button
                key={`${normalizedOptionValue}-${index}`}
                className={`dropdown-item filter-select__option ${isSelected ? "selected" : ""}`}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => handleSelect(opt.value)}
              >
                <span className="filter-select__option-label">{opt.label}</span>
                {isSelected ? <span className="filter-select__check">✓</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function EmployeesPage({ currentUserEmail, isAdminUser = false, canManageUsers = isAdminUser, ...rest }) {
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeesError, setEmployeesError] = useState("");

  const [leaves, setLeaves] = useState([]);
  const [allLeaves, setAllLeaves] = useState([]);
  const [leavesLoading, setLeavesLoading] = useState(false);
  const [leavesError, setLeavesError] = useState("");
  const [filters, setFilters] = useState({ status: "", type: "", employeeId: "" });
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [activeTab, setActiveTab] = useState(isAdminUser ? "employees" : "leaves");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [savingLeave, setSavingLeave] = useState(false);
  const [actioningId, setActioningId] = useState("");
  const lastDrawerSignalRef = useRef(rest.openDrawerSignal);

  const [userDrawerOpen, setUserDrawerOpen] = useState(false);
  const [userDrawerError, setUserDrawerError] = useState("");
  const [userDrawerSaving, setUserDrawerSaving] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDrawerMode, setUserDrawerMode] = useState("create");

  const currentUserId = useMemo(() => {
    if (!currentUserEmail) return null;
    const normalized = currentUserEmail.toLowerCase().trim();
    const match = employees.find((emp) => (emp.email || "").toLowerCase().trim() === normalized);
    return match?.id ? String(match.id) : null;
  }, [currentUserEmail, employees]);

  const fetchEmployees = async () => {
    setEmployeesLoading(true);
    setEmployeesError("");
    try {
      const data = await listUsers();
      setEmployees(Array.isArray(data) ? data : []);
    } catch (err) {
      setEmployeesError(err?.message || "Unable to load employees.");
    } finally {
      setEmployeesLoading(false);
    }
  };

  const loadLeaves = useCallback(
    async (activeFilters = filters) => {
      setLeavesLoading(true);
      setLeavesError("");
      try {
        const actor = {
          role: isAdminUser ? "admin" : "employee",
          id: currentUserId,
        };
        const data = await listLeaves(
          isAdminUser ? { ...activeFilters, status: "" } : { ...activeFilters, employeeId: currentUserId || activeFilters.employeeId },
          actor
        );
        const parsed = Array.isArray(data) ? data : [];
        setLeaves(parsed);
        const isUnfiltered =
          !activeFilters.employeeId && !activeFilters.type && (!activeFilters.status || activeFilters.status === "");
        if (isAdminUser && isUnfiltered) {
          setAllLeaves(parsed);
        } else if (!isAdminUser) {
          setAllLeaves(parsed);
        }
      } catch (err) {
        setLeavesError(err?.message || "Unable to load leaves.");
      } finally {
        setLeavesLoading(false);
      }
    },
    [currentUserId, filters, isAdminUser]
  );

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (!isAdminUser && !currentUserId) return;
    loadLeaves(filters);
  }, [filters, isAdminUser, currentUserId, loadLeaves]);

  const employeeLookup = useMemo(() => {
    const map = new Map();
    employees.forEach((emp) => {
      const key = emp?.id == null ? "" : String(emp.id);
      if (key) map.set(key, emp);
    });
    return map;
  }, [employees]);

  useEffect(() => {
    if (!isAdminUser && currentUserId) {
      setFilters((prev) => ({ ...prev, employeeId: currentUserId }));
    }
  }, [isAdminUser, currentUserId]);

  useEffect(() => {
    setActiveTab((prev) => {
      if (!isAdminUser) return "leaves";
      return prev === "employees" || prev === "leaves" ? prev : "employees";
    });
  }, [isAdminUser]);

  useEffect(() => {
    if (!isAdminUser) return;
    if (activeTab !== "leaves" && drawerOpen) {
      setDrawerOpen(false);
      setSelectedLeave(null);
      setDrawerMode("create");
    }
  }, [activeTab, drawerOpen, isAdminUser]);

  useEffect(() => {
    if (!isAdminUser || !canManageUsers) {
      lastDrawerSignalRef.current = rest.openDrawerSignal;
      return;
    }
    if (rest.openDrawerSignal === lastDrawerSignalRef.current) return;
    lastDrawerSignalRef.current = rest.openDrawerSignal;
    setActiveTab("employees");
    setSelectedUser(null);
    setUserDrawerMode("create");
    setUserDrawerError("");
    setUserDrawerOpen(true);
  }, [canManageUsers, isAdminUser, rest.openDrawerSignal]);

  const handleOpenCreate = () => {
    setLeavesError("");
    setSelectedLeave(null);
    setDrawerMode("create");
    setDrawerOpen(true);
  };

  const handleSubmitLeave = async (form) => {
    if (!currentUserId) {
      setLeavesError("Unable to submit leave: missing your employee id.");
      return;
    }
    setSavingLeave(true);
    try {
      const actor = { role: isAdminUser ? "admin" : "employee", id: currentUserId };
      const payload = {
        ...form,
        employeeId: isAdminUser ? form.employeeId : currentUserId,
        status: isAdminUser ? form.status : "pending",
      };
      if (drawerMode === "edit" && selectedLeave?.id) {
        const updated = await updateLeave(
          selectedLeave.id,
          {
            ...payload,
            approvedBy: payload.status === "approved" ? currentUserId : null,
          },
          actor
        );
        setLeaves((prev) => prev.map((leave) => (leave.id === selectedLeave.id ? { ...leave, ...updated } : leave)));
        setAllLeaves((prev) => prev.map((leave) => (leave.id === selectedLeave.id ? { ...leave, ...updated } : leave)));
      } else {
        const created = await createLeave(
          {
            ...payload,
            approvedBy: payload.status === "approved" ? currentUserId : null,
          },
          actor
        );
        setLeaves((prev) => [created, ...prev]);
        setAllLeaves((prev) => [created, ...prev]);
      }
      setDrawerOpen(false);
      setSelectedLeave(null);
      setDrawerMode("create");
    } catch (err) {
      setLeavesError(err?.message || "Unable to save leave.");
    } finally {
      setSavingLeave(false);
    }
  };

  const handleRowClick = (leave) => {
    setSelectedLeave(leave);
    setDrawerMode("edit");
    setDrawerOpen(true);
  };

  const handleUpdateStatus = async (leave, status) => {
    if (!isAdminUser) return;
    if (!leave?.id) return;
    setActioningId(leave.id);
    try {
      const actor = { role: "admin", id: currentUserId };
      const updated = await updateLeave(
        leave.id,
        {
          status,
          approvedBy: status === "approved" ? currentUserId : null,
        },
        actor
      );
      setLeaves((prev) => prev.map((row) => (row.id === leave.id ? { ...row, ...updated } : row)));
      setAllLeaves((prev) => prev.map((row) => (row.id === leave.id ? { ...row, ...updated } : row)));
    } catch (err) {
      setLeavesError(err?.message || "Unable to update leave status.");
    } finally {
      setActioningId("");
    }
  };

  const handleFilterChange = (field) => (nextValue) => {
    const normalized = nextValue == null ? "" : String(nextValue);
    setFilters((prev) => ({ ...prev, [field]: normalized }));
  };

  const filteredLeaves = useMemo(() => {
    return leaves.filter((leave) => {
      if (filters.employeeId && String(leave.employeeId) !== String(filters.employeeId)) return false;
      if (filters.type && leave.type !== filters.type) return false;
      if (filters.status && leave.status !== filters.status) return false;
      return true;
    });
  }, [leaves, filters]);

  const sortedLeaves = useMemo(() => {
    const order = { pending: 0, approved: 1, denied: 2, rejected: 2, cancelled: 2 };
    if (!filteredLeaves.length) return filteredLeaves;
    return [...filteredLeaves].sort((a, b) => {
      const diff = (order[a?.status] ?? 99) - (order[b?.status] ?? 99);
      if (diff !== 0) return diff;
      const aDate = a?.startDate ? new Date(a.startDate).getTime() : 0;
      const bDate = b?.startDate ? new Date(b.startDate).getTime() : 0;
      return bDate - aDate;
    });
  }, [filteredLeaves]);

  const handleMonthChange = (offset) => {
    setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + offset, 1));
  };

  const handleEmployeeRowClick = (row) => {
    if (!isAdminUser || !canManageUsers) return;
    const user = row?.__user || employees.find((emp) => (emp?.id || emp?.email) === (row?.id || row?.employeeId));
    if (!user) {
      setEmployeesError("Unable to open this employee.");
      return;
    }
    setSelectedUser(user);
    setUserDrawerMode("edit");
    setUserDrawerError("");
    setUserDrawerOpen(true);
  };

  const handleSubmitUser = async (form) => {
    if (!isAdminUser || !canManageUsers) return;
    setUserDrawerSaving(true);
    setUserDrawerError("");

    const basePayload = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      eligibility: Array.isArray(form.eligibility) ? Array.from(new Set(form.eligibility.filter(Boolean))) : [],
      role: (form.role || "employee").toLowerCase(),
    };

    if (userDrawerMode === "edit") {
      const userId = selectedUser?.id;
      if (!userId) {
        setUserDrawerError("Missing user id.");
        setUserDrawerSaving(false);
        return;
      }
      try {
        const response = await updateUser(userId, basePayload);
        const updatedUser = response?.user || response;
        setEmployees((prev) => prev.map((user) => (user.id === userId ? updatedUser : user)));
        setSelectedUser(updatedUser);
        setUserDrawerOpen(false);
      } catch (err) {
        setUserDrawerError(err?.message || "Unable to update user.");
      } finally {
        setUserDrawerSaving(false);
      }
      return;
    }

    try {
      const response = await createUser({
        ...basePayload,
        email: form.email.trim().toLowerCase(),
      });
      const createdUser = response?.user || response;
      setEmployees((prev) => [createdUser, ...prev]);
      setSelectedUser(createdUser);
      setUserDrawerOpen(false);
    } catch (err) {
      setUserDrawerError(err?.message || "Unable to create user.");
    } finally {
      setUserDrawerSaving(false);
    }
  };

  const monthLabel = useMemo(
    () => calendarMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" }),
    [calendarMonth]
  );

  const calendarDays = useMemo(() => {
    const firstDay = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
    const lastDay = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0);
    const gridStart = new Date(firstDay);
    gridStart.setDate(gridStart.getDate() - gridStart.getDay());
    const gridEnd = new Date(lastDay);
    gridEnd.setDate(gridEnd.getDate() + (6 - gridEnd.getDay()));

    const days = [];
    for (let cursor = new Date(gridStart); cursor <= gridEnd; cursor.setDate(cursor.getDate() + 1)) {
      days.push({
        date: new Date(cursor),
        inMonth: cursor.getMonth() === calendarMonth.getMonth(),
      });
    }
    return days;
  }, [calendarMonth]);

  const todayKey = useMemo(() => formatDateKey(new Date()), []);

  const calendarEventsByDay = useMemo(() => {
    if (!isAdminUser) return new Map();

    const monthStart = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
    const monthEnd = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0);
    const gridStart = new Date(monthStart);
    gridStart.setDate(gridStart.getDate() - gridStart.getDay());
    const gridEnd = new Date(monthEnd);
    gridEnd.setDate(gridEnd.getDate() + (6 - gridEnd.getDay()));
    const order = { pending: 0, approved: 1 };
    const map = new Map();

    leaves
      .filter((leave) => leave && (leave.status === "approved" || leave.status === "pending"))
      .forEach((leave) => {
        const start = toDayDate(leave.startDate);
        const end = toDayDate(leave.endDate);
        if (!start || !end) return;
        if (end < gridStart || start > gridEnd) return;

        const rangeStart = start < gridStart ? gridStart : start;
        const rangeEnd = end > gridEnd ? gridEnd : end;
        const person = leave.employee || employeeLookup.get(String(leave.employeeId));
        const name = person?.name || person?.email || "Unknown employee";

        for (let cursor = new Date(rangeStart); cursor <= rangeEnd; cursor.setDate(cursor.getDate() + 1)) {
          const key = formatDateKey(cursor);
          const existing = map.get(key) || [];
          map.set(
            key,
            [...existing, { id: leave.id, name, status: leave.status, type: leave.type }].sort((a, b) => {
              const diff = (order[a.status] ?? 99) - (order[b.status] ?? 99);
              return diff !== 0 ? diff : a.name.localeCompare(b.name);
            })
          );
        }
      });

    return map;
  }, [calendarMonth, employeeLookup, isAdminUser, leaves]);

  const visibleEmployees = useMemo(() => {
    if (isAdminUser) return employees;
    if (!currentUserId) return [];
    return employees.filter((emp) => String(emp.id) === currentUserId);
  }, [employees, isAdminUser, currentUserId]);

  const employeeOptions = useMemo(() => {
    const base = [{ value: "", label: isAdminUser ? "All employees" : "You" }];
    visibleEmployees.forEach((emp) => {
      if (!emp?.id) return;
      base.push({
        value: String(emp.id),
        label: emp.name || emp.email || "Unnamed employee",
      });
    });
    return base;
  }, [visibleEmployees, isAdminUser]);

  const typeOptions = useMemo(
    () => [
      { value: "", label: "All" },
      { value: "annual", label: "Annual vacation" },
      { value: "sick", label: "Sick leave" },
    ],
    []
  );

  const statusOptions = useMemo(
    () => [
      { value: "", label: isAdminUser ? "All" : "Your requests" },
      { value: "pending", label: "Pending" },
      { value: "approved", label: "Approved" },
      { value: "denied", label: "Rejected" },
    ],
    [isAdminUser]
  );

  const leavesForUsage = useMemo(() => {
    if (!isAdminUser) return leaves;
    return allLeaves.length ? allLeaves : leaves;
  }, [allLeaves, isAdminUser, leaves]);

  const leaveUsageByEmployee = useMemo(() => {
    const allowances = new Map();
    employees.forEach((emp) => {
      const id = emp?.id == null ? "" : String(emp.id);
      if (!id) return;
      const annualRaw =
        emp.leaveBalance ??
        emp.leaveAllowance ??
        emp.annualLeaveBalance ??
        emp.annualLeaveDays ??
        emp.leaveBalanceDays;
      const sickRaw = emp.sickLeaveBalance ?? emp.sickLeaveDays ?? emp.sickLeaveAllowance;
      const parsedAnnual = Number(annualRaw);
      const parsedSick = Number(sickRaw);
      const annualTotal =
        !Number.isNaN(parsedAnnual) && parsedAnnual > 0 ? Math.round(parsedAnnual) : DEFAULT_ANNUAL_LEAVE_DAYS;
      const sickTotal = !Number.isNaN(parsedSick) && parsedSick > 0 ? Math.round(parsedSick) : DEFAULT_SICK_LEAVE_DAYS;
      allowances.set(id, { annual: annualTotal, sick: sickTotal });
    });

    const usage = new Map();
    const getAllowance = (id) =>
      allowances.get(id) || { annual: DEFAULT_ANNUAL_LEAVE_DAYS, sick: DEFAULT_SICK_LEAVE_DAYS };

    leavesForUsage.forEach((leave) => {
      if (!leave || leave.status !== "approved") return;
      const id = leave.employeeId ? String(leave.employeeId) : "";
      if (!id) return;
      const duration = getDurationDays(leave.startDate, leave.endDate);
      if (!duration) return;
      const allowance = getAllowance(id);
      const bucket = leave.type === "sick" ? "sick" : "annual";
      const totalForType = allowance[bucket] || (bucket === "sick" ? DEFAULT_SICK_LEAVE_DAYS : DEFAULT_ANNUAL_LEAVE_DAYS);

      const current = usage.get(id) || {
        annual: { used: 0, total: allowance.annual, remaining: allowance.annual },
        sick: { used: 0, total: allowance.sick, remaining: allowance.sick },
      };

      const existing = current[bucket] || { used: 0, total: totalForType, remaining: totalForType };
      const used = existing.used + duration;
      const remaining = Math.max(0, (existing.total ?? totalForType) - used);

      const next = {
        annual: current.annual || { used: 0, total: allowance.annual, remaining: allowance.annual },
        sick: current.sick || { used: 0, total: allowance.sick, remaining: allowance.sick },
      };
      next[bucket] = { used, total: existing.total ?? totalForType, remaining };
      usage.set(id, next);
    });

    employees.forEach((emp) => {
      const id = emp?.id == null ? "" : String(emp.id);
      if (!id || usage.has(id)) return;
      const allowance = getAllowance(id);
      usage.set(id, {
        annual: { used: 0, total: allowance.annual, remaining: allowance.annual },
        sick: { used: 0, total: allowance.sick, remaining: allowance.sick },
      });
    });

    return usage;
  }, [employees, leavesForUsage]);

  const employeeTableColumns = useMemo(
    () => [
      { key: "name", label: "Name" },
      { key: "phone", label: "Phone", width: "150px" },
      { key: "email", label: "Email", width: "220px" },
      { key: "balance", label: "Leave balance (Annual / Sick)", width: "220px" },
      { key: "approvedUsed", label: "Approved leaves (A / S)", width: "200px" },
      { key: "status", label: "Status", width: "130px" },
    ],
    []
  );

  const employeeTableRows = useMemo(() => {
    return employees.map((emp) => {
      const id = emp?.id == null ? "" : String(emp.id);
      const stats = id ? leaveUsageByEmployee.get(id) || null : null;
      const annual = stats?.annual || { used: 0, total: DEFAULT_ANNUAL_LEAVE_DAYS, remaining: DEFAULT_ANNUAL_LEAVE_DAYS };
      const sick = stats?.sick || { used: 0, total: DEFAULT_SICK_LEAVE_DAYS, remaining: DEFAULT_SICK_LEAVE_DAYS };
      const balanceLabel = `A: ${annual.remaining ?? 0}d / ${annual.total ?? DEFAULT_ANNUAL_LEAVE_DAYS}d • S: ${sick.remaining ?? 0}d / ${sick.total ?? DEFAULT_SICK_LEAVE_DAYS}d`;
      return {
        id: id || emp.email || emp.name,
        name: emp.name || "—",
        phone: emp.phone || "—",
        email: emp.email || "—",
        balance: balanceLabel,
        approvedUsed: `A: ${annual.used ?? 0}d • S: ${sick.used ?? 0}d`,
        status: { type: "status", label: emp.active === false ? "Inactive" : "Active", tone: emp.active === false ? "danger" : "success" },
        __user: emp,
      };
    });
  }, [employees, leaveUsageByEmployee]);

  const canEditUsers = isAdminUser && canManageUsers;

  const leaveRequestsContent = (
    <>
      {leavesLoading ? (
        <div className="inline-alert info compact">
          <span>Loading leaves...</span>
        </div>
      ) : null}

      {!leavesLoading && !(isAdminUser ? sortedLeaves.length : filteredLeaves.length) ? (
        <div className="inline-alert compact info">
          <span>No leaves match these filters yet.</span>
        </div>
      ) : null}

      {(isAdminUser ? sortedLeaves.length : filteredLeaves.length) ? (
        isAdminUser ? (
          <div className="leave-requests-list">
            {sortedLeaves.map((leave) => {
              const employee = leave.employee || employeeLookup.get(String(leave.employeeId));
              const approver = leave.approver || (leave.approvedBy ? employeeLookup.get(String(leave.approvedBy)) : null);
              const duration = getDurationDays(leave.startDate, leave.endDate);
              return (
                <button
                  key={leave.id}
                  className="leave-card"
                  type="button"
                  onClick={() => handleRowClick(leave)}
                  aria-label="Open leave"
                >
                  <div className="leave-card__top">
                    <div>
                      <div className="leave-title">{employee?.name || employee?.email || "Unknown employee"}</div>
                      <div className="muted extra-small">{TYPE_LABELS[leave.type] || leave.type}</div>
                    </div>
                    <span className={`pill status ${leave.status}`}>
                      {STATUS_LABELS[leave.status] || leave.status}
                    </span>
                  </div>
                  <div className="leave-card__dates">
                    <span>{formatDate(leave.startDate)}</span>
                    <span className="muted extra-small">to</span>
                    <span>{formatDate(leave.endDate)}</span>
                  </div>
                  <div className="leave-card__meta">
                    <span className="muted extra-small">Duration</span>
                    <span className="leave-card__value">{duration ? `${duration}d` : "—"}</span>
                  </div>
                  <div className="leave-card__meta">
                    <span className="muted extra-small">Approver</span>
                    <span className="leave-card__value">{approver?.name || approver?.email || "—"}</span>
                  </div>
                  {isAdminUser && leave.status === "pending" ? (
                    <div className="leave-card__actions">
                      <button
                        className="ghost-btn small"
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleUpdateStatus(leave, "approved");
                        }}
                        disabled={actioningId === leave.id || leave.status === "approved"}
                      >
                        {actioningId === leave.id && leave.status !== "approved" ? "Updating..." : "Approve"}
                      </button>
                      <button
                        className="ghost-btn small"
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleUpdateStatus(leave, "denied");
                        }}
                        disabled={actioningId === leave.id || leave.status === "denied"}
                      >
                        Deny
                      </button>
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="leave-table">
            <div className="leave-row leave-head-row">
              <div>Employee</div>
              <div>Type</div>
              <div>Date range</div>
              <div>Duration</div>
              <div>Status</div>
              <div>Approver</div>
              <div>Actions</div>
            </div>
            {filteredLeaves.map((leave) => {
              const employee = leave.employee || employeeLookup.get(String(leave.employeeId));
              const approver = leave.approver || (leave.approvedBy ? employeeLookup.get(String(leave.approvedBy)) : null);
              const duration = getDurationDays(leave.startDate, leave.endDate);
              return (
                <button
                  key={leave.id}
                  className="leave-row"
                  type="button"
                  onClick={() => handleRowClick(leave)}
                  aria-label="Open leave"
                >
                  <div data-label="Employee">
                    <div className="leave-title">{employee?.name || employee?.email || "Unknown employee"}</div>
                    <div className="muted extra-small">{employee?.email}</div>
                  </div>
                  <div data-label="Type">{TYPE_LABELS[leave.type] || leave.type}</div>
                  <div data-label="Date range">
                    <div>{formatDate(leave.startDate)} - {formatDate(leave.endDate)}</div>
                  </div>
                  <div data-label="Duration">{duration ? `${duration}d` : "—"}</div>
                  <div data-label="Status">
                    <span className={`pill status small ${leave.status}`}>
                      {STATUS_LABELS[leave.status] || leave.status}
                    </span>
                  </div>
                  <div data-label="Approver">{approver?.name || approver?.email || "—"}</div>
                  {isAdminUser && leave.status === "pending" ? (
                    <div className="leave-actions" data-label="Actions">
                      <button
                        className="ghost-btn small"
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleUpdateStatus(leave, "approved");
                        }}
                        disabled={actioningId === leave.id || leave.status === "approved"}
                      >
                        {actioningId === leave.id && leave.status !== "approved" ? "Updating..." : "Approve"}
                      </button>
                      <button
                        className="ghost-btn small"
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleUpdateStatus(leave, "denied");
                        }}
                        disabled={actioningId === leave.id || leave.status === "denied"}
                      >
                        Deny
                      </button>
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>
        )
      ) : null}
    </>
  );

  const leavePanel = (
    <article className="panel leave-panel">
      <header className="panel-header leave-head">
        <div>
          <h2>Leave management</h2>
          <p className="muted small">Track annual vacation and sick leave across employees.</p>
        </div>
        <button className="primary-btn" type="button" onClick={handleOpenCreate}>
          + New leave
        </button>
      </header>

      <div className="leave-filters">
        <FilterSelect
          label="Employee"
          value={filters.employeeId}
          onChange={handleFilterChange("employeeId")}
          options={employeeOptions}
          disabled={!isAdminUser}
          placeholder={isAdminUser ? "All employees" : "You"}
        />
        <FilterSelect
          label="Type"
          value={filters.type}
          onChange={handleFilterChange("type")}
          options={typeOptions}
          placeholder="All"
        />
        <FilterSelect
          label="Status"
          value={filters.status}
          onChange={handleFilterChange("status")}
          options={statusOptions}
          placeholder={isAdminUser ? "All" : "Your requests"}
        />
        <button className="ghost-btn small" type="button" onClick={() => loadLeaves(filters)} disabled={leavesLoading}>
          {leavesLoading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {leavesError ? (
        <div className="inline-alert error compact">
          <span>{leavesError}</span>
          <button className="ghost-btn small" type="button" onClick={() => setLeavesError("")}>
            Dismiss
          </button>
        </div>
      ) : null}
      {employeesError ? (
        <div className="inline-alert error compact">
          <span>{employeesError}</span>
          <button className="ghost-btn small" type="button" onClick={() => setEmployeesError("")}>
            Dismiss
          </button>
        </div>
      ) : null}

      {isAdminUser ? (
        <div className="leave-layout">
          <section className="leave-calendar">
            <header className="leave-calendar__head">
              <div>
                <div className="leave-calendar__title">Team leave calendar</div>
                <div className="muted extra-small">
                  Approved days show in green. Pending requests stay yellow until you decide.
                </div>
              </div>
              <div className="leave-calendar__nav">
                <button className="ghost-btn small" type="button" onClick={() => handleMonthChange(-1)}>
                  &lt; Prev
                </button>
                <div className="leave-calendar__month">{monthLabel}</div>
                <button className="ghost-btn small" type="button" onClick={() => handleMonthChange(1)}>
                  Next &gt;
                </button>
              </div>
            </header>
            <div className="calendar-scroll">
              <div className="calendar-grid calendar-weekdays">
                {CALENDAR_WEEKDAYS.map((day) => (
                  <div key={day} className="calendar-weekday">
                    {day}
                  </div>
                ))}
              </div>
              <div className="calendar-grid">
                {calendarDays.map((day) => {
                  const key = formatDateKey(day.date);
                  const events = calendarEventsByDay.get(key) || [];
                  const isToday = key === todayKey;
                  return (
                    <div
                      key={`${key}-${day.inMonth ? "in" : "out"}`}
                      className={`calendar-day${day.inMonth ? "" : " muted"}${isToday ? " today" : ""}`}
                    >
                      <div className="calendar-day__top">
                        <span className="calendar-date">{day.date.getDate()}</span>
                        {!day.inMonth ? (
                          <span className="calendar-day__month">
                            {day.date.toLocaleString(undefined, { month: "short" })}
                          </span>
                        ) : null}
                        {isToday ? <span className="calendar-today-pill">Today</span> : null}
                      </div>
                      <div className="calendar-events">
                        {events.length ? (
                          events.slice(0, 3).map((event, index) => (
                            <div key={`${event.id || event.name}-${index}`} className={`calendar-event ${event.status}`}>
                              <span className="calendar-event__dot" aria-hidden="true" />
                              <span className="calendar-event__name">
                                {event.name}
                                <span className="calendar-event__type"> · {TYPE_LABELS[event.type] || event.type || "Leave"}</span>
                              </span>
                            </div>
                          ))
                        ) : (
                          <div className="calendar-empty" aria-hidden="true" />
                        )}
                        {events.length > 3 ? (
                          <div className="calendar-more muted extra-small">+{events.length - 3} more</div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="leave-requests">
            <div className="leave-requests__head">
              <div>
                <div className="leave-requests__title">Leave requests</div>
                <div className="muted extra-small">Latest submissions from the team.</div>
              </div>
              <div className="pill subtle">{sortedLeaves.length} shown</div>
            </div>
            {leaveRequestsContent}
          </section>
        </div>
      ) : (
        leaveRequestsContent
      )}
    </article>
  );

  const employeesPanel = (
    <article className="panel">
      <header className="panel-header">
        <div>
          <h2>Employees</h2>
          <p className="muted small">Contact info, leave balance, and active status.</p>
        </div>
      </header>
      {employeesLoading ? (
        <div className="inline-alert info compact">
          <span>Loading employees...</span>
        </div>
      ) : null}
      {employeesError ? (
        <div className="inline-alert error compact">
          <span>{employeesError}</span>
          <button className="ghost-btn small" type="button" onClick={() => setEmployeesError("")}>
            Dismiss
          </button>
        </div>
      ) : null}
      {employeeTableRows.length ? (
        <div className="table-wrap employees-table">
          <Table
            columns={employeeTableColumns}
            rows={employeeTableRows}
            enableSelection={false}
            onRowClick={canEditUsers ? handleEmployeeRowClick : undefined}
          />
        </div>
      ) : !employeesLoading ? (
        <div className="inline-alert compact info">
          <span>No employees found.</span>
        </div>
      ) : null}
    </article>
  );

  const settingsProps = {
    ...rest,
    currentUserEmail,
    showCompanyPanel: false,
    canManageUsers,
    leaveUsageById: leaveUsageByEmployee,
    showLeaveUsage: true,
  };

  const leavesTabContent = (
    <section className="settings-grid">
      {leavePanel}
    </section>
  );

  const employeesTabContent = (
    <section className="settings-grid">
      {employeesPanel}
    </section>
  );

  const content = isAdminUser ? (
    <div className="employees-tabs">
      <div className="employee-tablist" role="tablist" aria-label="Employees sections">
        <button
          type="button"
          className={`employee-tab ${activeTab === "employees" ? "active" : ""}`}
          onClick={() => setActiveTab("employees")}
          role="tab"
          aria-selected={activeTab === "employees"}
        >
          Employees
        </button>
        <button
          type="button"
          className={`employee-tab ${activeTab === "leaves" ? "active" : ""}`}
          onClick={() => setActiveTab("leaves")}
          role="tab"
          aria-selected={activeTab === "leaves"}
        >
          Leaves
        </button>
      </div>
      {activeTab === "employees" ? employeesTabContent : null}
      {activeTab === "leaves" ? leavesTabContent : null}
    </div>
  ) : (
    <SettingsPage {...settingsProps} extraPanels={leavePanel} />
  );

  return (
    <>
      {content}
      <LeaveDrawer
        open={drawerOpen}
        onClose={() => {
          if (savingLeave) return;
          setDrawerOpen(false);
          setSelectedLeave(null);
          setDrawerMode("create");
        }}
        onSubmit={handleSubmitLeave}
        saving={savingLeave}
        error={leavesError}
        employees={visibleEmployees}
        isAdmin={isAdminUser}
        lockedEmployeeId={isAdminUser ? "" : currentUserId}
        leave={drawerMode === "edit" ? selectedLeave : null}
      />
      <UserDrawer
        open={userDrawerOpen}
        onClose={() => {
          if (userDrawerSaving) return;
          setUserDrawerOpen(false);
          setSelectedUser(null);
          setUserDrawerError("");
          setUserDrawerMode("create");
        }}
        onSubmit={handleSubmitUser}
        saving={userDrawerSaving}
        error={userDrawerError}
        accessOptions={ACCESS_OPTIONS}
        user={selectedUser}
        mode={userDrawerMode}
        withOverlay
      />
    </>
  );
}

export default EmployeesPage;
